/**
 * Pure model for the PERSONAL task list («Мои задачи»). No React — unit-tested.
 *
 * Three status sections (Запланировано / В работе / Сделано); inside an active
 * section the rows are chunked into contiguous *urgency groups* — overdue,
 * today, one group per future date, then no-date.
 *
 * The manual drag-and-drop order (`position`) survives WITHOUT any arithmetic
 * here: the server already returns the feed in `position` order, and
 * `Array.prototype.sort` is stable, so sorting by urgency alone preserves the
 * relative order inside every group. Do not "fix" this by sorting on
 * `position` — the board would then fight the server's own ordering.
 */
import { MoveStaffTaskInput } from 'core/graphql';

import {
  IStaffTask,
  DONE,
  bucketByStatus,
  isClosed,
  addDays,
} from './taskBuckets';

/** Status sections of the personal list, in render order. */
export type TListSection = 'open' | 'in_progress' | 'done';
export const LIST_SECTIONS: TListSection[] = ['open', 'in_progress', 'done'];

/** How soon a row is due. `dated` groups carry the date in their header, so
 *  they have no locale key of their own — see `URGENCY_LABEL_KEYS`. */
export type TUrgency = 'overdue' | 'today' | 'dated' | 'noDate';
export const URGENCY_KEYS: TUrgency[] = ['overdue', 'today', 'dated', 'noDate'];
/** The urgency groups that render a translated header. */
export const URGENCY_LABEL_KEYS: TUrgency[] = ['overdue', 'today', 'noDate'];

/** Toolbar chips, shared by the personal list and the manager board. */
export const TASK_FILTERS = ['all', 'today', 'overdue', 'noDate'] as const;
export type TTaskFilter = typeof TASK_FILTERS[number];

export const TASK_MODES = ['mine', 'team'] as const;
export type TTaskMode = typeof TASK_MODES[number];

// Виды живут в `tasksViewModel` (ТЗ 0062): их стало пять, и держать список в
// двух местах — верный способ получить кнопку, чей URL при перезагрузке молча
// сбрасывается. Импортировать их НАДО оттуда напрямую: реэкспорт отсюда
// замкнёт цикл (`tasksViewModel` берёт здесь `urgencyOf`), а react-refresh
// в dev перебирает экспорты модуля и упрётся в ещё не инициализированный
// `const` — «Cannot access 'TASK_VIEWS' before initialization». Сборка и jest
// это НЕ ловят: react-refresh есть только в dev-сервере.

export const urgencyOf = (task: IStaffTask, today: string): TUrgency => {
  if (!task.dueDate) return 'noDate';
  if (task.dueDate < today) return 'overdue';
  if (task.dueDate === today) return 'today';
  return 'dated';
};

/** Rank used for the stable sort. Mirrors the order of `URGENCY_KEYS`. */
export const urgencyRank = (task: IStaffTask, today: string): number =>
  URGENCY_KEYS.indexOf(urgencyOf(task, today));

/**
 * Identity of the urgency group a task belongs to. Every future date is its
 * OWN group (`d:2025-08-15`), so reordering never mixes two deadlines.
 */
export const groupKeyOf = (task: IStaffTask, today: string): string => {
  const urgency = urgencyOf(task, today);
  return urgency === 'dated' ? `d:${task.dueDate}` : urgency;
};

/** `${section}:${groupKey}` — the rbd droppable id. Split on the FIRST colon
 *  only: a dated group key contains one (`d:2025-08-15`). */
export const droppableIdOf = (
  section: TListSection,
  groupKey: string,
): string => `${section}:${groupKey}`;

export const parseDroppableId = (
  id: string,
): { section: TListSection; groupKey: string } | null => {
  const at = id.indexOf(':');
  if (at < 0) return null;
  const section = id.slice(0, at) as TListSection;
  if (!LIST_SECTIONS.includes(section)) return null;
  return { section, groupKey: id.slice(at + 1) };
};

export interface IListGroup {
  /** Urgency group key: `overdue` | `today` | `d:<iso>` | `noDate`. */
  key: string;
  urgency: TUrgency;
  /** Set for `dated` groups — the header renders this date. */
  dueDate: string | null;
  droppableId: string;
  tasks: IStaffTask[];
}

export interface IListSectionModel {
  key: TListSection;
  count: number;
  groups: IListGroup[];
}

export interface IBuildListOptions {
  /** Reveal `cancelled` rows inside the closed section. */
  showCancelled?: boolean;
  /**
   * Не разбивать секции на группы срочности и НЕ сортировать внутри них.
   * Включается при явной сортировке: иначе внутренняя сортировка по срочности
   * затёрла бы выбранный оператором порядок, и «по приоритету» молча
   * превратилось бы в «по срочности, потом по приоритету».
   */
  flat?: boolean;
}

/** Chips → predicate. `all` keeps everything; the rest narrow by urgency. */
export const filterTasks = (
  tasks: IStaffTask[],
  filter: TTaskFilter,
  today: string,
): IStaffTask[] => {
  if (filter === 'all') return tasks;
  return tasks.filter((task) => {
    const urgency = urgencyOf(task, today);
    if (filter === 'overdue') return urgency === 'overdue' && !isClosed(task);
    if (filter === 'today') return urgency === 'today';
    return urgency === 'noDate';
  });
};

const CLOSED_GROUP_KEY = 'closed';

/**
 * Build the rendered model. `tasks` MUST arrive in the server's feed order
 * (`position` first) — see the note at the top of this file.
 */
export const buildTaskList = (
  tasks: IStaffTask[],
  today: string,
  { showCancelled = false, flat = false }: IBuildListOptions = {},
): IListSectionModel[] => {
  const byStatus = bucketByStatus(tasks);

  const activeSection = (key: 'open' | 'in_progress'): IListSectionModel => {
    if (flat) {
      const rows = byStatus[key];
      return {
        key,
        count: rows.length,
        groups: rows.length
          ? [
              {
                key: 'flat',
                urgency: 'noDate',
                dueDate: null,
                droppableId: droppableIdOf(key, 'flat'),
                tasks: rows,
              },
            ]
          : [],
      };
    }
    // Sort by urgency; the date is a tiebreaker ONLY between `dated` rows, so
    // that the future-date groups read nearest-first. It must not fire inside
    // `overdue` (which spans many dates) — that would silently re-sort a group
    // whose order the operator sets by hand.
    const rows = [...byStatus[key]].sort((a, b) => {
      const byUrgency = urgencyRank(a, today) - urgencyRank(b, today);
      if (byUrgency !== 0) return byUrgency;
      if (urgencyOf(a, today) !== 'dated') return 0;
      return (a.dueDate || '').localeCompare(b.dueDate || '');
    });
    const groups: IListGroup[] = [];
    rows.forEach((task) => {
      const groupKey = groupKeyOf(task, today);
      const last = groups[groups.length - 1];
      if (last && last.key === groupKey) {
        last.tasks.push(task);
        return;
      }
      const urgency = urgencyOf(task, today);
      groups.push({
        key: groupKey,
        urgency,
        dueDate: urgency === 'dated' ? task.dueDate : null,
        droppableId: droppableIdOf(key, groupKey),
        tasks: [task],
      });
    });
    return { key, count: rows.length, groups };
  };

  // Closed rows carry no manual order (position is meaningless once done), so
  // they read newest-first. Cancelled hides behind the toggle.
  const closed = byStatus.done
    .filter((task) => showCancelled || task.status === DONE)
    .sort((a, b) =>
      (b.completedAt || b.updatedAt || '').localeCompare(
        a.completedAt || a.updatedAt || '',
      ),
    );

  return [
    activeSection('open'),
    activeSection('in_progress'),
    {
      key: 'done',
      count: closed.length,
      groups: closed.length
        ? [
            {
              key: CLOSED_GROUP_KEY,
              urgency: 'noDate',
              dueDate: null,
              droppableId: droppableIdOf('done', CLOSED_GROUP_KEY),
              tasks: closed,
            },
          ]
        : [],
    },
  ];
};

export interface IDropArgs {
  draggableId: string;
  source: { droppableId: string; index: number };
  destination: { droppableId: string; index: number };
  /** The model currently on screen (source of the neighbours). */
  sections: IListSectionModel[];
  /** The flat feed, in server order — the base for the optimistic snapshot. */
  tasks: IStaffTask[];
}

export interface IDropPlan {
  input: MoveStaffTaskInput;
  /** Optimistic feed held until the move's refetch lands. */
  nextTasks: IStaffTask[];
}

const findGroup = (
  sections: IListSectionModel[],
  droppableId: string,
): IListGroup | undefined =>
  sections
    .reduce<IListGroup[]>((acc, section) => acc.concat(section.groups), [])
    .find((group) => group.droppableId === droppableId);

/**
 * Translate a drop into a `moveStaffTask` input plus the optimistic feed.
 * Returns `null` for a drop that must snap back.
 *
 * Legal drops: reorder inside one group, or the same urgency group in another
 * ACTIVE section (that is a status change). A drop into another urgency group
 * is refused — urgency is derived from the due date, so "move it to today"
 * would silently need a reschedule the operator did not ask for. Rescheduling
 * lives on the manager board, whose columns ARE deadlines.
 */
export const listDropPlan = ({
  draggableId,
  source,
  destination,
  sections,
  tasks,
}: IDropArgs): IDropPlan | null => {
  const from = parseDroppableId(source.droppableId);
  const to = parseDroppableId(destination.droppableId);
  if (!from || !to) return null;
  // Completing is the checkbox, never a drag — and a closed row has no rank.
  if (from.section === 'done' || to.section === 'done') return null;
  if (from.groupKey !== to.groupKey) return null;
  if (
    source.droppableId === destination.droppableId &&
    source.index === destination.index
  ) {
    return null;
  }

  const dragged = tasks.find((task) => task.id === draggableId);
  if (!dragged) return null;

  // Neighbours INSIDE the destination group only: `position` is a global rank,
  // so borrowing a neighbour from the next group would interleave the two.
  const destGroup = findGroup(sections, destination.droppableId);
  const destIds = (destGroup?.tasks || [])
    .filter((task) => task.id !== draggableId)
    .map((task) => task.id);
  destIds.splice(destination.index, 0, draggableId);
  const at = destIds.indexOf(draggableId);
  // The board is ascending by position: `before` sits ABOVE, `after` BELOW.
  const beforeId = destIds[at - 1];
  const afterId = destIds[at + 1];

  const input: MoveStaffTaskInput = { id: draggableId };
  if (beforeId) input.beforeId = beforeId;
  if (afterId) input.afterId = afterId;

  const crossSection = from.section !== to.section;
  if (crossSection) input.targetStatus = to.section;

  const patched: IStaffTask = crossSection
    ? { ...dragged, status: to.section, completedAt: null }
    : dragged;

  const others = tasks.filter((task) => task.id !== draggableId);
  let insertAt = others.length;
  if (afterId) {
    const i = others.findIndex((task) => task.id === afterId);
    if (i >= 0) insertAt = i;
  } else if (beforeId) {
    const i = others.findIndex((task) => task.id === beforeId);
    if (i >= 0) insertAt = i + 1;
  }

  return {
    input,
    nextTasks: [
      ...others.slice(0, insertAt),
      patched,
      ...others.slice(insertAt),
    ],
  };
};

/** Local `YYYY-MM-DD` a day from now — used by the quick-due chips. */
export const tomorrowOf = (today: string): string => addDays(today, 1);
